import { HttpClient, HttpErrorResponse, HttpParams, HttpStatusCode } from "@angular/common/http";
import { Inject, Injectable, NgZone } from "@angular/core";
import { Observable, Subscriber, catchError, map, of, throwError } from "rxjs";
import { TREASURY_ENV } from "../../config/micros-env.tokens";
import { EnvMicroTreasury } from "../../config/env.micro.treasury";
import { SessionCtl } from "../../services/session.ctl.service";
import { WalletSseParser } from "../../parsers/wallet-sse.parser";
import {
  ReconciliationBankBalance,
  ReconciliationBankBreakdown,
  ReconciliationBankMonthlyBreakdown,
  ReconciliationCountryBreakdown,
  ReconciliationCountryMonthlyBreakdown,
  ReconciliationDashboardSummary,
  ReconciliationMonthlySummary,
  ReconciliationPaymentOption,
  ReconciliationPaymentPage,
  ReconciliationProvider,
  ReconciliationPaymentQuery,
  ReconciliationValidatedPaymentQuery,
  ReconciliationPackageBreakdown,
  ReconciliationPackageMonthlyBreakdown,
  ReconciliationPendingRecognition,
} from "../../../domain/models/accounting/reconciliation-dashboard.model";
import { ReconciliationDashboardPort } from "../../../app/ports/accounting/reconciliation-dashboard.port";
import {
  toReconciliationBankBalance,
  toReconciliationBankBreakdown,
  toReconciliationBankMonthlyBreakdown,
  toReconciliationCountryBreakdown,
  toReconciliationCountryMonthlyBreakdown,
  toReconciliationDashboardSummary,
  toReconciliationMonthlySummaries,
  toReconciliationPaymentOptions,
  toReconciliationPaymentPage,
  toReconciliationProviders,
  toReconciliationPackageBreakdown,
  toReconciliationPackageMonthlyBreakdown,
  toReconciliationPendingRecognition,
} from "../../validators/accounting/reconciliation-dashboard.validator";

const STREAM_UNAVAILABLE = "El stream del dashboard de conciliación no respondió correctamente.";
const INVALID_MONTHLY_SUMMARIES = "La respuesta del resumen mensual de conciliación es inválida.";
const INVALID_BANK_BALANCE = "La respuesta del saldo del banco es inválida.";
const INVALID_PAYMENTS = "La respuesta de los pagos del mes es inválida.";
const INVALID_PAYMENT_OPTIONS = "La respuesta de los tipos de banco y de pago es inválida.";
const INVALID_PROVIDERS = "La respuesta de los proveedores de macro es inválida.";

interface DashboardScreen<T> {
  readonly path: string;
  readonly invalidResponse: string;
  readonly parse: (value: unknown) => T | null;
}

const SUMMARY_SCREEN: DashboardScreen<ReconciliationDashboardSummary> = {
  path: "/summary",
  invalidResponse: "La respuesta del resumen de conciliación es inválida.",
  parse: toReconciliationDashboardSummary,
};

const BANK_SCREEN: DashboardScreen<ReconciliationBankBreakdown> = {
  path: "/by-bank",
  invalidResponse: "La respuesta del detalle por banco es inválida.",
  parse: toReconciliationBankBreakdown,
};

const BANK_MONTHLY_SCREEN: DashboardScreen<ReconciliationBankMonthlyBreakdown> = {
  path: "/by-bank-monthly",
  invalidResponse: "La respuesta del detalle mensual por banco es inválida.",
  parse: toReconciliationBankMonthlyBreakdown,
};

const PACKAGE_SCREEN: DashboardScreen<ReconciliationPackageBreakdown> = {
  path: "/by-package",
  invalidResponse: "La respuesta del detalle por membresía es inválida.",
  parse: toReconciliationPackageBreakdown,
};

const COUNTRY_SCREEN: DashboardScreen<ReconciliationCountryBreakdown> = {
  path: "/by-country",
  invalidResponse: "La respuesta del detalle por país es inválida.",
  parse: toReconciliationCountryBreakdown,
};

const PACKAGE_MONTHLY_SCREEN: DashboardScreen<ReconciliationPackageMonthlyBreakdown> = {
  path: "/by-package-monthly",
  invalidResponse: "La respuesta del detalle mensual por membresía es inválida.",
  parse: toReconciliationPackageMonthlyBreakdown,
};

const COUNTRY_MONTHLY_SCREEN: DashboardScreen<ReconciliationCountryMonthlyBreakdown> = {
  path: "/by-country-monthly",
  invalidResponse: "La respuesta del detalle mensual por país es inválida.",
  parse: toReconciliationCountryMonthlyBreakdown,
};

const PENDING_RECOGNITION_SCREEN: DashboardScreen<ReconciliationPendingRecognition> = {
  path: "/pending-recognition",
  invalidResponse: "La respuesta de los pendientes por reconocer es inválida.",
  parse: toReconciliationPendingRecognition,
};

@Injectable()
export class ReconciliationDashboardHttpAdapter implements ReconciliationDashboardPort {
  private static readonly DASHBOARD_PATH = "/api/v1/reconciliation-dashboard";
  private static readonly STREAM_SUFFIX = "/stream";
  private static readonly MONTHLY_SUMMARY_PATH = "/api/v1/reconciliation-monthly-summary";
  private static readonly BANK_BALANCE_PATH = "/api/v1/reconciliation-bank-balance";
  private static readonly PAYMENTS_PATH = "/api/v1/reconciliation-payments";
  private static readonly VALIDATED_PAYMENTS_PATH = "/api/v1/reconciliation-validated-payments";
  private static readonly PAYMENT_OPTIONS_SUFFIX = "/payment-options";
  private static readonly PROVIDERS_SUFFIX = "/providers";

  constructor(
    private readonly http: HttpClient,
    @Inject(TREASURY_ENV) private readonly env: EnvMicroTreasury,
    private readonly sessionCtl: SessionCtl,
    private readonly zone: NgZone,
  ) {}

  summary(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationDashboardSummary | null> {
    return this.latest(SUMMARY_SCREEN, companyCode, currencyCode, periodKey);
  }

  summaryUpdates(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationDashboardSummary> {
    return this.updates(SUMMARY_SCREEN, companyCode, currencyCode, periodKey);
  }

  monthlySummaries(companyCode: string, currencyCode: string): Observable<ReconciliationMonthlySummary[]> {
    return this.http
      .get<unknown>(`${this.baseUrl()}${ReconciliationDashboardHttpAdapter.MONTHLY_SUMMARY_PATH}`, {
        params: this.params(companyCode, currencyCode),
      })
      .pipe(
        map((response) => {
          const months = toReconciliationMonthlySummaries(response);
          if (!months) throw new Error(INVALID_MONTHLY_SUMMARIES);
          return months;
        }),
      );
  }

  bankBalance(companyCode: string, currencyCode: string): Observable<ReconciliationBankBalance | null> {
    return this.http
      .get<unknown>(`${this.baseUrl()}${ReconciliationDashboardHttpAdapter.BANK_BALANCE_PATH}`, {
        params: this.params(companyCode, currencyCode),
      })
      .pipe(
        map((response) => {
          if (response === null) return null;
          const balance = toReconciliationBankBalance(response);
          if (!balance) throw new Error(INVALID_BANK_BALANCE);
          return balance;
        }),
      );
  }

  bankBreakdown(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationBankBreakdown | null> {
    return this.latest(BANK_SCREEN, companyCode, currencyCode, periodKey);
  }

  bankBreakdownUpdates(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationBankBreakdown> {
    return this.updates(BANK_SCREEN, companyCode, currencyCode, periodKey);
  }

  bankMonthlyBreakdown(
    companyCode: string,
    currencyCode: string,
  ): Observable<ReconciliationBankMonthlyBreakdown | null> {
    return this.latest(BANK_MONTHLY_SCREEN, companyCode, currencyCode);
  }

  bankMonthlyBreakdownUpdates(
    companyCode: string,
    currencyCode: string,
  ): Observable<ReconciliationBankMonthlyBreakdown> {
    return this.updates(BANK_MONTHLY_SCREEN, companyCode, currencyCode);
  }

  packageBreakdown(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationPackageBreakdown | null> {
    return this.latest(PACKAGE_SCREEN, companyCode, currencyCode, periodKey);
  }

  packageBreakdownUpdates(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationPackageBreakdown> {
    return this.updates(PACKAGE_SCREEN, companyCode, currencyCode, periodKey);
  }

  countryBreakdown(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationCountryBreakdown | null> {
    return this.latest(COUNTRY_SCREEN, companyCode, currencyCode, periodKey);
  }

  countryBreakdownUpdates(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationCountryBreakdown> {
    return this.updates(COUNTRY_SCREEN, companyCode, currencyCode, periodKey);
  }

  packageMonthlyBreakdown(
    companyCode: string,
    currencyCode: string,
  ): Observable<ReconciliationPackageMonthlyBreakdown | null> {
    return this.latest(PACKAGE_MONTHLY_SCREEN, companyCode, currencyCode);
  }

  packageMonthlyBreakdownUpdates(
    companyCode: string,
    currencyCode: string,
  ): Observable<ReconciliationPackageMonthlyBreakdown> {
    return this.updates(PACKAGE_MONTHLY_SCREEN, companyCode, currencyCode);
  }

  countryMonthlyBreakdown(
    companyCode: string,
    currencyCode: string,
  ): Observable<ReconciliationCountryMonthlyBreakdown | null> {
    return this.latest(COUNTRY_MONTHLY_SCREEN, companyCode, currencyCode);
  }

  countryMonthlyBreakdownUpdates(
    companyCode: string,
    currencyCode: string,
  ): Observable<ReconciliationCountryMonthlyBreakdown> {
    return this.updates(COUNTRY_MONTHLY_SCREEN, companyCode, currencyCode);
  }

  payments(query: ReconciliationPaymentQuery): Observable<ReconciliationPaymentPage> {
    const params = this.params(query.companyCode, query.currencyCode)
      .set("yearMonth", query.yearMonth)
      .set("dimension", query.dimension)
      .set("key", query.key)
      .set("status", query.status)
      .set("page", query.page)
      .set("size", query.size);
    return this.http
      .get<unknown>(`${this.baseUrl()}${ReconciliationDashboardHttpAdapter.PAYMENTS_PATH}`, { params })
      .pipe(
        map((response) => {
          const page = toReconciliationPaymentPage(response);
          if (!page) throw new Error(INVALID_PAYMENTS);
          return page;
        }),
      );
  }

  validatedPayments(query: ReconciliationValidatedPaymentQuery): Observable<ReconciliationPaymentPage> {
    let params = this.params(query.companyCode, query.currencyCode)
      .set("yearMonth", query.yearMonth)
      .set("page", query.page)
      .set("size", query.size);
    if (query.day !== null) params = params.set("day", query.day);
    if (query.countryCode) params = params.set("countryCode", query.countryCode);
    if (query.processTypeId !== null) params = params.set("processTypeId", query.processTypeId);
    if (query.familyId !== null) params = params.set("familyId", query.familyId);
    if (query.installmentType) params = params.set("installmentType", query.installmentType);
    if (query.paymentTypeId !== null) params = params.set("paymentTypeId", query.paymentTypeId);
    if (query.paymentSubtypeId !== null) params = params.set("paymentSubtypeId", query.paymentSubtypeId);
    if (query.search) params = params.set("search", query.search);
    if (query.providerCode) params = params.set("providerCode", query.providerCode);
    return this.http
      .get<unknown>(`${this.baseUrl()}${ReconciliationDashboardHttpAdapter.VALIDATED_PAYMENTS_PATH}`, { params })
      .pipe(
        map((response) => {
          const page = toReconciliationPaymentPage(response);
          if (!page) throw new Error(INVALID_PAYMENTS);
          return page;
        }),
      );
  }

  validatedPaymentProviders(companyCode: string, currencyCode: string): Observable<ReconciliationProvider[]> {
    const path = `${ReconciliationDashboardHttpAdapter.VALIDATED_PAYMENTS_PATH}${ReconciliationDashboardHttpAdapter.PROVIDERS_SUFFIX}`;
    return this.http
      .get<unknown>(`${this.baseUrl()}${path}`, { params: this.params(companyCode, currencyCode) })
      .pipe(
        map((response) => {
          const providers = toReconciliationProviders(response);
          if (!providers) throw new Error(INVALID_PROVIDERS);
          return providers;
        }),
      );
  }

  paymentOptions(
    companyCode: string,
    currencyCode: string,
    yearMonth: string,
  ): Observable<ReconciliationPaymentOption[]> {
    const path = `${ReconciliationDashboardHttpAdapter.VALIDATED_PAYMENTS_PATH}${ReconciliationDashboardHttpAdapter.PAYMENT_OPTIONS_SUFFIX}`;
    return this.http
      .get<unknown>(`${this.baseUrl()}${path}`, {
        params: this.params(companyCode, currencyCode).set("yearMonth", yearMonth),
      })
      .pipe(
        map((response) => {
          const options = toReconciliationPaymentOptions(response);
          if (!options) throw new Error(INVALID_PAYMENT_OPTIONS);
          return options;
        }),
      );
  }

  pendingRecognition(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationPendingRecognition | null> {
    return this.latest(PENDING_RECOGNITION_SCREEN, companyCode, currencyCode, periodKey);
  }

  pendingRecognitionUpdates(
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<ReconciliationPendingRecognition> {
    return this.updates(PENDING_RECOGNITION_SCREEN, companyCode, currencyCode, periodKey);
  }

  private latest<T>(
    screen: DashboardScreen<T>,
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<T | null> {
    const params = this.params(companyCode, currencyCode, periodKey);
    return this.http.get<unknown>(this.url(screen.path), { params }).pipe(
      map((response) => {
        const parsed = screen.parse(response);
        if (!parsed) throw new Error(screen.invalidResponse);
        return parsed;
      }),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === HttpStatusCode.NotFound
          ? of(null)
          : throwError(() => error),
      ),
    );
  }

  private updates<T>(
    screen: DashboardScreen<T>,
    companyCode: string,
    currencyCode: string,
    periodKey?: string,
  ): Observable<T> {
    const streamPath = `${screen.path}${ReconciliationDashboardHttpAdapter.STREAM_SUFFIX}`;
    const streamUrl = `${this.url(streamPath)}?${this.params(companyCode, currencyCode, periodKey).toString()}`;
    return new Observable<T>((subscriber) => {
      const controller = new AbortController();
      this.follow(screen, streamUrl, controller.signal, subscriber, 0);
      return () => controller.abort();
    });
  }

  private follow<T>(
    screen: DashboardScreen<T>,
    streamUrl: string,
    signal: AbortSignal,
    subscriber: Subscriber<T>,
    attempt: number,
  ): void {
    void Promise.resolve()
      .then(() => fetch(streamUrl, { headers: this.requestHeaders(), signal }))
      .then((response) => this.read(screen, response, signal, subscriber))
      .then(
        () => 0,
        () => attempt + 1,
      )
      .then((nextAttempt) => {
        if (signal.aborted) return;
        const delay = Math.min(
          this.env.reconnectInitialDelayMs * 2 ** nextAttempt,
          this.env.reconnectMaxDelayMs,
        );
        setTimeout(() => this.follow(screen, streamUrl, signal, subscriber, nextAttempt), delay);
      });
  }

  private async read<T>(
    screen: DashboardScreen<T>,
    response: Response,
    signal: AbortSignal,
    subscriber: Subscriber<T>,
  ): Promise<void> {
    if (!response.ok || !response.body) throw new Error(STREAM_UNAVAILABLE);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parser = new WalletSseParser();
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) return;
      parser.push(decoder.decode(value, { stream: true }))
        .map((event) => screen.parse(JSON.parse(event.data) as unknown))
        .filter((parsed): parsed is T => parsed !== null)
        .forEach((parsed) => this.zone.run(() => subscriber.next(parsed)));
    }
  }

  private params(companyCode: string, currencyCode: string, periodKey?: string): HttpParams {
    const params = new HttpParams().set("companyCode", companyCode).set("currencyCode", currencyCode);
    return periodKey ? params.set("periodKey", periodKey) : params;
  }

  private requestHeaders(): HeadersInit {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (this.env.authMode === "bearer") {
      headers["Authorization"] = `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`;
    }
    return headers;
  }

  private url(screenPath: string): string {
    return `${this.baseUrl()}${ReconciliationDashboardHttpAdapter.DASHBOARD_PATH}${screenPath}`;
  }

  private baseUrl(): string {
    return `${this.env.host}${this.env.apiBasePath}`.replace(/\/$/, "");
  }
}
